import { useCallback, useEffect, useState } from 'react';
import { getToken, onMessage } from 'firebase/messaging';
import { useIsAuthenticated } from '@/shared/auth/store';
import { useToast } from '@/shared/ui/Toast';
import { apiErrorMessage } from '@/shared/api/client';
import { getMessagingIfSupported } from './firebase';
import { registerPushToken, unregisterPushToken } from './api';

const PUSH_ENABLED = import.meta.env.VITE_PUSH_ENABLED === 'true';
const VAPID_KEY = import.meta.env.VITE_FIREBASE_VAPID_KEY;
const SW_URL = '/firebase-messaging-sw.js';
const SW_SCOPE = '/firebase-push/';

// 마지막으로 등록한 토큰 — 로그아웃 시 다른 컴포넌트에서도 해제할 수 있게 모듈 스코프에 보관.
let lastRegisteredToken: string | null = null;

type Permission = NotificationPermission | 'unsupported';

function currentPermission(): Permission {
  if (typeof Notification === 'undefined') return 'unsupported';
  return Notification.permission;
}

/** FCM 푸시 SW 를 좁은 스코프로 등록 (vite-plugin-pwa /sw.js 와 공존). */
async function registerPushServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return null;
  try {
    return await navigator.serviceWorker.register(SW_URL, { scope: SW_SCOPE });
  } catch {
    return null;
  }
}

/** 토큰을 발급받아 백엔드에 등록. 실패 시 null. */
async function acquireAndRegisterToken(): Promise<string | null> {
  const messaging = await getMessagingIfSupported();
  if (!messaging) return null;
  const registration = await registerPushServiceWorker();
  if (!registration) return null;
  const token = await getToken(messaging, {
    vapidKey: VAPID_KEY,
    serviceWorkerRegistration: registration,
  });
  if (!token) return null;
  await registerPushToken(token);
  lastRegisteredToken = token;
  return token;
}

interface UsePushRegistration {
  permission: Permission;
  enablePush: () => Promise<void>;
  isRegistering: boolean;
}

/**
 * 로그인 사용자에게 웹 푸시(FCM)를 연결한다.
 * - VITE_PUSH_ENABLED!=='true', 미인증, 미지원 환경에선 전부 no-op.
 * - 권한이 이미 'granted' 면 마운트 시 조용히 토큰을 재등록(백엔드 갱신).
 * - enablePush() 로 권한 요청 → 토큰 발급 → 등록.
 * - 포그라운드 메시지는 토스트로 표시.
 * 어떤 경우에도 앱을 깨뜨리지 않는다(try/catch).
 */
export function usePushRegistration(): UsePushRegistration {
  const isAuthenticated = useIsAuthenticated();
  const toast = useToast();
  const [permission, setPermission] = useState<Permission>(currentPermission);
  const [isRegistering, setIsRegistering] = useState(false);
  const active = PUSH_ENABLED && isAuthenticated && permission !== 'unsupported';

  const enablePush = useCallback(async () => {
    if (!active || isRegistering) return;
    setIsRegistering(true);
    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      if (result !== 'granted') {
        toast.show('알림 권한이 허용되지 않았어요.');
        return;
      }
      const token = await acquireAndRegisterToken();
      if (token) {
        toast.success('푸시 알림을 켰어요.');
      } else {
        toast.error('이 브라우저에서는 푸시 알림을 쓸 수 없어요.');
      }
    } catch (err) {
      toast.error(apiErrorMessage(err, '푸시 알림 설정에 실패했어요.'));
    } finally {
      setIsRegistering(false);
    }
  }, [active, isRegistering, toast]);

  // 마운트/인증 시: 권한이 이미 허용돼 있으면 조용히 재등록(토큰 갱신).
  useEffect(() => {
    if (!active || permission !== 'granted') return;
    void (async () => {
      try {
        await acquireAndRegisterToken();
      } catch {
        // 조용히 무시 — 자동 재등록 실패가 앱을 막으면 안 됨
      }
    })();
  }, [active, permission]);

  // 포그라운드 메시지 → 토스트
  useEffect(() => {
    if (!active) return;
    let unsubscribe: (() => void) | undefined;
    void (async () => {
      try {
        const messaging = await getMessagingIfSupported();
        if (!messaging) return;
        unsubscribe = onMessage(messaging, (payload) => {
          const title = payload.notification?.title ?? '모이자';
          const body = payload.notification?.body;
          toast.show(body ? `${title} · ${body}` : title);
        });
      } catch {
        // 무시
      }
    })();
    return () => {
      unsubscribe?.();
    };
  }, [active, toast]);

  return { permission, enablePush, isRegistering };
}

/** 로그아웃 등에서 마지막으로 등록한 토큰을 best-effort 로 해제. */
export async function unregisterLastPushToken(): Promise<void> {
  const token = lastRegisteredToken;
  if (!token) return;
  lastRegisteredToken = null;
  try {
    await unregisterPushToken(token);
  } catch {
    // best-effort — 실패해도 로그아웃을 막지 않음
  }
}
